import {
    SxProps,
    Box,
    Typography,
    CardActionArea,
    useTheme
} from '@mui/material'
import { LightHrEvent, TSpecialty } from '@pturf-betin/betin-models-lib'
import { FlexBoxSpaceBetween, FlexBox } from 'components/atoms'
import { useBetContext } from 'contexts/BetContext'
import { useTranslation } from 'contexts/TranslationContext'
import { ClockIcon, DisciplineIcon } from 'icons'
import { getRemainingTimeText, removeDuplicates } from 'utils'

export const NavigationRow = ({
    upcomingEvents,
    rowType = 'MEETING',
    selectedMeetingNumber,
    selectedRaceNumber,
    selectedDateTime,
    selectedSpecialty,
    onClick,
    sx = {}
}: {
    upcomingEvents: LightHrEvent[]
    selectedMeetingNumber?: string
    selectedRaceNumber?: string
    selectedSpecialty?: TSpecialty
    selectedDateTime?: string
    rowType?: 'MEETING' | 'RACE'
    onClick?: (eventNumber: number) => void
    sx?: SxProps
}) => {
    const theme = useTheme()
    const translation = useTranslation()
    const { selectedOperator, mainColor } = useBetContext()

    const isMeeting = rowType === 'MEETING'
    const prefix = isMeeting ? 'R' : 'C'
    const selectedNumber = isMeeting
        ? selectedMeetingNumber
        : selectedRaceNumber
    const selectedName = isMeeting
        ? upcomingEvents.find(
              (event) => event.cNumber === selectedMeetingNumber
          )?.cName
        : upcomingEvents.find(
              (event) =>
                  event.cNumber === selectedMeetingNumber &&
                  event.nb === selectedRaceNumber
          )?.name

    const availableMeetings: string[] = removeDuplicates(
        upcomingEvents.map((event) => event.cNumber!)
    ).sort((a, b) => {
        // 0 sort after anything else
        if (a === '0') {
            return 1
        }
        if (b === '0') {
            return -1
        }
        // otherwise, ascending numeric sort
        return a < b ? -1 : 1
    })

    const availableRaces: string[] = removeDuplicates(
        upcomingEvents
            .filter((event) => event.cNumber === selectedMeetingNumber)
            .map((event) => event.nb!)
    ).sort((a, b) => +a - +b)

    return (
        <Box sx={sx}>
            {selectedMeetingNumber && (
                <FlexBoxSpaceBetween sx={{ mb: 0.5 }}>
                    <FlexBox>
                        <FlexBox
                            sx={{
                                width: 26,
                                height: 18,
                                backgroundColor: mainColor,
                                justifyContent: 'center',
                                borderRadius: 1
                            }}
                        >
                            <Typography
                                variant="title-14-bold"
                                sx={{ fontSize: 12, color: 'white' }}
                                data-testid={
                                    prefix === 'R'
                                        ? 'rl-nav-current-meeting'
                                        : 'rl-nav-current-race'
                                }
                            >
                                {prefix}
                                {selectedNumber}
                            </Typography>
                        </FlexBox>
                        <Typography
                            component={'div'}
                            sx={{
                                color: theme.palette.text.secondary,
                                ml: 0.75,
                                display: 'flex',
                                flex: 1
                            }}
                            variant="text-12-semi-bold"
                        >
                            {selectedName}
                        </Typography>
                    </FlexBox>
                    {isMeeting && (
                        <FlexBox>
                            {selectedSpecialty && (
                                <>
                                    <DisciplineIcon
                                        disc={selectedSpecialty}
                                        fill={theme.palette.text.secondary}
                                        size={20}
                                    />
                                    <Typography
                                        sx={{
                                            color: theme.palette.grey['400'],
                                            mx: 0.5
                                        }}
                                        variant="text-16-regular"
                                    >
                                        |
                                    </Typography>
                                </>
                            )}
                            {selectedDateTime && (
                                <>
                                    <Typography
                                        variant="text-12-regular"
                                        sx={{
                                            mr: 0.5,
                                            whiteSpace: 'nowrap',
                                            color: theme.palette.text.secondary
                                        }}
                                    >
                                        {getRemainingTimeText(
                                            selectedDateTime,
                                            translation.imminent
                                        )}
                                    </Typography>
                                    <ClockIcon
                                        fill={theme.palette.text.secondary}
                                    />
                                </>
                            )}
                        </FlexBox>
                    )}
                </FlexBoxSpaceBetween>
            )}
            <FlexBox sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {(isMeeting ? availableMeetings : availableRaces).map(
                    (eventNumber) => {
                        const isSelected = eventNumber === selectedNumber
                        return (
                            <Box key={`nav-${rowType}-${eventNumber}`}>
                                <CardActionArea
                                    onClick={() =>
                                        onClick && onClick(+eventNumber)
                                    }
                                    sx={{
                                        borderRadius: 1
                                    }}
                                    data-testid="rl-nav-button"
                                >
                                    <FlexBox
                                        sx={{
                                            width: 30,
                                            height: 22,
                                            justifyContent: 'center',
                                            border: `1px solid ${
                                                isSelected
                                                    ? theme.operators[
                                                          selectedOperator
                                                      ]?.backgroundColor
                                                    : 'white'
                                            }`,
                                            backgroundColor: 'white',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography
                                            variant="text-12-regular"
                                            sx={{
                                                color: isSelected
                                                    ? theme.operators[
                                                          selectedOperator
                                                      ]?.backgroundColor
                                                    : theme.palette.text
                                                          .secondary
                                            }}
                                        >
                                            {prefix}
                                            {eventNumber}
                                        </Typography>
                                    </FlexBox>
                                </CardActionArea>
                            </Box>
                        )
                    }
                )}
            </FlexBox>
        </Box>
    )
}
